import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCalendar } from "../../../../context/CalendarContext";
import { useSchedulingFocus } from "../../../../context/SchedulingFocusContext";
import type { CalendarEventType, SlotInfo } from "../../../../types/calendar";
import type { SalonService } from "../../../../types/salon";
import type {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../types/transactions";
import { transformTransactionResponseToGetTransactionsResponse } from "../../../../utils/transactionUtils";
import { SNAP_INCREMENT_MINS, snapToIncrement } from "../calendarDragHelpers";
import type { CalendarDropInfo } from "../components/Views/DnDCalendar";
import type { DrawerSlotApi } from "../drawer/drawerSlot";
import { wallClockParts } from "../components/TransactionDetailModal/RebookModal/schedulingCalendarUtils";
import {
  BookingDraftAction,
  BookingDraftState,
  bookingDraftReducer,
  initialBookingDraft,
  isDraftTouched,
  routeCalendarEventClick,
} from "./bookingDraftReducer";
import { seedFromTransaction } from "./bookingSeed";
import { notifyError } from "../../../../utils/notify";
import {
  blockedClickToast,
  blockedDropToast,
  blockedStaffFor,
} from "./skillBlock";
import { buildTempEvents, tempEventRowUid } from "./bookingTempEvents";
import {
  ServiceRow,
  dateOfDay,
  dayOf,
  emptyRow,
  isDayInView,
} from "./bookingTypes";

type ShownStaff = { id: string; first_name?: string; last_name?: string };

const staffDisplayName = (shownStaff: ShownStaff[], id: string): string => {
  const e = shownStaff.find((x) => x.id === id);
  return e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || id : id;
};

/** A day-view staff column id, if it is one of the shown providers. */
const staffColumn = (
  resourceId: string | undefined,
  shownStaff: { id: string }[],
): string | undefined =>
  resourceId && shownStaff.some((e) => e.id === resourceId)
    ? resourceId
    : undefined;

interface UseBookingSessionArgs {
  timezone: string;
  salonServices: SalonService[];
  isMobile: boolean;
  /** Stash the open preview popover and close it (popover-launched entry). */
  stashAndClosePreview: () => void;
  /** Re-open the stashed preview popover (no-op when nothing is stashed). */
  reopenPreview: () => void;
  /** Forget the stashed popover (its appointment just moved). */
  dropPreviewStash: () => void;
  /** Open an event's preview popover anchored to the clicked element. */
  openEventPreview: (
    event: CalendarEventType,
    anchorEl: HTMLElement | null,
  ) => void;
  storeTransaction: (transaction: GetTransactionsResponse) => void;
  refetchAndUpdateTransaction: (
    transactionId: string,
    errorContext: string,
  ) => Promise<void>;
  /** The calendar's single drawer slot; an open session holds it as "booking". */
  drawerSlot: Pick<DrawerSlotApi, "open" | "close" | "setEvictor">;
}

/**
 * The booking drawer's session: one draft (bookingDraftReducer) plus its
 * open/close lifecycle, the discard gate, return-to-origin on close, the
 * calendar routing while open, and the SchedulingFocus publish.
 *
 * The draft owns its booking day; CalendarContext.selectedDate is only the
 * viewed day. Toolbar / date picker / week navigation change what is viewed,
 * never the draft. The draft's day changes only from a slot click, a
 * temp-event drop, or the form (date field, suggested time). Where the view
 * should follow, the SAME handler also navigates it: opening a reschedule /
 * rebook shows the draft's day once, and a form date change shows the new
 * day (a slot click / drop is already on screen). There is no state→state
 * mirror effect in either direction.
 *
 * The draft lives in a ref-shadowed store: `run(action)` applies the reducer
 * to the LATEST state synchronously (so two updates in one tick compose) and
 * publishes the result with a plain setState(value). Nothing runs inside a
 * React state updater.
 *
 * Drawer slot: opening the session takes the slot as "booking" (evicting the
 * preview popover), closing releases it, and another kind taking the slot
 * closes the session as is (no discard prompt: event clicks reach the slot
 * only after routeCalendarEventClick's discard gate).
 */
export function useBookingSession({
  timezone,
  salonServices,
  isMobile,
  stashAndClosePreview,
  reopenPreview,
  dropPreviewStash,
  openEventPreview,
  storeTransaction,
  refetchAndUpdateTransaction,
  drawerSlot,
}: UseBookingSessionArgs) {
  const { selectedDate, setSelectedDate, viewType, filteredEmployees } =
    useCalendar();
  const { setFocus: setSchedulingFocus } = useSchedulingFocus();

  // ---- Draft store (ref-shadowed reducer) ---------------------------------
  const [state, setState] = useState<BookingDraftState>(initialBookingDraft);
  const stateRef = useRef<BookingDraftState>(state);
  const { open: openSlot, close: closeSlot, setEvictor } = drawerSlot;
  const run = useCallback(
    (action: BookingDraftAction): BookingDraftState => {
      const prev = stateRef.current;
      const next = bookingDraftReducer(prev, action);
      if (next !== prev) {
        stateRef.current = next;
        setState(next);
        // The session's open/close is the slot's "booking" open/close.
        if (next.open && !prev.open) openSlot("booking", next.mode);
        if (!next.open && prev.open) closeSlot("booking");
      }
      return next;
    },
    [openSlot, closeSlot],
  );

  // ---- The viewed day (CalendarContext) + latest inputs for stable handlers
  const viewedDay = dayOf(selectedDate);
  // Latest-value refs, refreshed every render (same pattern as DayView's
  // getOverridesRef) so the handlers below keep a stable identity.
  // `viewedDayRef` is also advanced by showDay so a second call in the same
  // tick sees it.
  const viewedDayRef = useRef(viewedDay);
  viewedDayRef.current = viewedDay;
  const inputs = {
    timezone,
    salonServices,
    filteredEmployees,
    reopenPreview,
    stashAndClosePreview,
    dropPreviewStash,
    openEventPreview,
    storeTransaction,
    refetchAndUpdateTransaction,
  };
  const inputsRef = useRef(inputs);
  // What runs once the discard confirm resolves with Discard: preview the
  // clicked real event (new booking), or the FAB action that interrupted
  // the draft (requestCloseThen). Set with the prompt; cleared on any exit.
  const pendingRef = useRef<(() => void) | null>(null);
  inputsRef.current = inputs;

  /** Navigate the VIEW to a day (never changes the draft). */
  const showDay = useCallback(
    (nextDay: string) => {
      if (!nextDay || nextDay === viewedDayRef.current) return;
      viewedDayRef.current = nextDay;
      setSelectedDate(dateOfDay(nextDay));
    },
    [setSelectedDate],
  );

  // ---- Open --------------------------------------------------------------
  // The calendar's staff filter is the user's alone: opening, slot clicks,
  // drops and provider picks never change it.
  const openSession = useCallback(
    (open: Omit<Extract<BookingDraftAction, { type: "open" }>, "type">) =>
      run({ type: "open", ...open }),
    [run],
  );

  /** FAB "New appointment" (desktop): an empty draft on the viewed day. */
  const startNewAppointment = useCallback(() => {
    openSession({
      mode: "create",
      origin: "calendar",
      sourceTxn: null,
      rows: [emptyRow(1, null)],
      clientUuid: "",
      day: viewedDayRef.current,
    });
  }, [openSession]);

  /**
   * Mobile FAB "New Appointment" / "Group booking": an empty draft on the
   * viewed day with no start time yet (the mobile draft asks for one).
   */
  const startBlankAppointment = useCallback(() => {
    openSession({
      mode: "create",
      origin: "calendar",
      sourceTxn: null,
      rows: [emptyRow(1, null, "")],
      clientUuid: "",
      day: viewedDayRef.current,
    });
  }, [openSession]);

  /**
   * Slot chooser → "Appointment" (desktop) / slot menu → "Appointment"
   * (mobile). The slot Date carries salon wall-clock as browser-local; the
   * slot is on screen, so the view stays where it is. A day-view staff column
   * pre-fills that provider (the staff filter is left alone).
   */
  const startFromSlot = useCallback(
    (slot: SlotInfo) => {
      const { date, time } = wallClockParts(slot.start);
      const staffId = staffColumn(
        slot.resourceId,
        inputsRef.current.filteredEmployees,
      );
      const row = emptyRow(1, null, time);
      if (staffId) row.employeeId = staffId;
      openSession({
        mode: "create",
        origin: "calendar",
        sourceTxn: null,
        rows: [row],
        clientUuid: "",
        day: date,
      });
    },
    [openSession],
  );

  /**
   * Popover Reschedule ("edit") / Rebook: seeded from the appointment. The
   * view moves to the draft's day once, here.
   */
  const startFromPopover = useCallback(
    (txn: GetTransactionsResponse, mode: "edit" | "rebook") => {
      const seed = seedFromTransaction(
        txn,
        mode,
        inputsRef.current.timezone,
        new Date(),
      );
      inputsRef.current.stashAndClosePreview();
      openSession({
        mode,
        origin: "popover",
        sourceTxn: txn,
        rows: seed.rows,
        clientUuid: seed.clientUuid,
        day: seed.day,
      });
      showDay(seed.day);
    },
    [openSession, showDay],
  );

  const startReschedule = useCallback(
    (txn: GetTransactionsResponse) => startFromPopover(txn, "edit"),
    [startFromPopover],
  );
  const startRebook = useCallback(
    (txn: GetTransactionsResponse) => startFromPopover(txn, "rebook"),
    [startFromPopover],
  );

  // ---- Close -------------------------------------------------------------
  /** Close unconditionally and return to where the session started. */
  const close = useCallback(() => {
    const closing = stateRef.current;
    pendingRef.current = null;
    if (!closing.open) return;
    run({ type: "close" });
    if (closing.origin === "popover") inputsRef.current.reopenPreview();
  }, [run]);

  // Another drawer took the slot: close as is. Nothing to return to (the
  // new drawer is the destination), so the popover stash is dropped.
  useEffect(() => {
    setEvictor("booking", () => {
      pendingRef.current = null;
      inputsRef.current.dropPreviewStash();
      run({ type: "close" });
    });
  }, [setEvictor, run]);

  /** Every interruption routes here: prompt only when the draft was edited. */
  const requestClose = useCallback(() => {
    pendingRef.current = null;
    if (isDraftTouched(stateRef.current)) {
      run({ type: "promptDiscard" });
    } else {
      close();
    }
  }, [run, close]);

  /** "Keep editing": the confirm closes, nothing else happens. */
  const dismissDiscard = useCallback(() => {
    pendingRef.current = null;
    run({ type: "dismissDiscard" });
  }, [run]);

  /**
   * "Discard": close the session, then run what raised the confirm (a real
   * event's preview, or an interrupting FAB action), if anything.
   */
  const discard = useCallback(() => {
    const pending = pendingRef.current;
    close();
    pending?.();
  }, [close]);

  /**
   * Run `next` (a FAB action) once the session is out of the way: right
   * away when closed; otherwise through the same gate as every interruption
   * (discard confirm only when the draft was edited). The popover stash is
   * dropped first, since `next` is where the user is going.
   */
  const requestCloseThen = useCallback(
    (next: () => void) => {
      if (!stateRef.current.open) {
        next();
        return;
      }
      inputsRef.current.dropPreviewStash();
      if (isDraftTouched(stateRef.current)) {
        pendingRef.current = next;
        run({ type: "promptDiscard" });
      } else {
        close();
        next();
      }
    },
    [run, close],
  );

  /** Create / rebook success: store the new transaction(s), then close. */
  const handleCreated = useCallback(
    (created: TransactionResponse[]) => {
      created.forEach((response) =>
        inputsRef.current.storeTransaction(
          transformTransactionResponseToGetTransactionsResponse(response),
        ),
      );
      close();
    },
    [close],
  );

  /**
   * Reschedule success. The appointment moved, so a stashed popover is stale:
   * drop it, close, then refetch the source (and any cross-day split
   * transactions) into the store.
   */
  const handleEditSuccess = useCallback(
    (splitTransactionIds: string[] = []) => {
      const sourceId = stateRef.current.sourceTxn?.transaction.id;
      inputsRef.current.dropPreviewStash();
      close();
      const { refetchAndUpdateTransaction: refetch } = inputsRef.current;
      [sourceId, ...splitTransactionIds]
        .filter((id): id is string => !!id)
        .forEach((id) => void refetch(id, "booking drawer edit"));
    },
    [close],
  );

  // ---- Form bindings -----------------------------------------------------
  /**
   * The form hands us an updater; it runs against the latest rows. A date
   * the form wrote onto the rows moves the draft to that day (reducer), and
   * this same handler shows that day so the temp events stay visible.
   */
  const handleRowsChange = useCallback(
    (update: (rows: ServiceRow[]) => ServiceRow[]) => {
      const prev = stateRef.current;
      const next = run({ type: "rowsUpdated", update });
      if (next.day !== prev.day) showDay(next.day);
    },
    [run, showDay],
  );

  const handleClientChange = useCallback(
    (clientUuid: string) => {
      run({ type: "clientChanged", clientUuid });
    },
    [run],
  );

  const handleActiveChange = useCallback(
    (uid: number) => {
      run({ type: "activeChanged", uid });
    },
    [run],
  );

  // ---- Calendar routing while open ---------------------------------------
  /**
   * Event click while open (routeCalendarEventClick): own temp event →
   * select its row; reschedule / rebook → other events do nothing; new
   * booking → close and preview the clicked event, via the discard confirm
   * when the draft was edited. The anchor is read now (a SyntheticEvent's
   * currentTarget is gone after dispatch).
   */
  const handleCalendarSelectEvent = useCallback(
    (event: CalendarEventType, e?: SyntheticEvent) => {
      const route = routeCalendarEventClick(stateRef.current, event);
      const anchorEl = (e?.currentTarget as HTMLElement | undefined) ?? null;
      switch (route.kind) {
        case "selectRow":
          run({ type: "activeChanged", uid: route.uid });
          return;
        case "closeAndPreview":
          close();
          inputsRef.current.openEventPreview(event, anchorEl);
          return;
        case "promptDiscard":
          pendingRef.current = () =>
            inputsRef.current.openEventPreview(event, anchorEl);
          run({ type: "promptDiscard" });
          return;
        default:
      }
    },
    [run, close],
  );

  /**
   * Empty slot → place the WHOLE appointment there: the first service at the
   * slot, the rest back to back (create, rebook and reschedule). Providers are
   * kept. A slot on another viewed day (week view) moves the draft to it.
   */
  const handleCalendarSelectSlot = useCallback(
    (slot: SlotInfo) => {
      if (!stateRef.current.open || !slot?.start) return;
      const { date, time } = wallClockParts(
        snapToIncrement(slot.start, SNAP_INCREMENT_MINS),
      );
      const { filteredEmployees: shown, salonServices: svcs } =
        inputsRef.current;
      const staffId = staffColumn(slot.resourceId, shown);
      // A column whose staff member doesn't offer the active service is
      // blocked: error toast, nothing moves.
      const active = stateRef.current.rows.find(
        (r) => r.uid === stateRef.current.activeUid,
      );
      const blocked = staffId && blockedStaffFor(active, svcs || [], [staffId]);
      if (blocked) {
        const t = blockedClickToast(
          blocked.serviceName,
          staffDisplayName(shown, staffId),
        );
        notifyError(t.note, { title: t.title });
        return;
      }
      run({
        type: "placeAllAtSlot",
        time,
        day: date,
        staffId,
        services: svcs,
      });
    },
    [run],
  );

  /** Temp-event drag → move that row; only temp events are draggable. */
  const handleCalendarDrop = useCallback(
    (info: CalendarDropInfo) => {
      const uid = tempEventRowUid(info.event);
      if (uid === null || !stateRef.current.open) return;
      const { date, time } = wallClockParts(
        snapToIncrement(info.start, SNAP_INCREMENT_MINS),
      );
      const { filteredEmployees: shown, salonServices: svcs } =
        inputsRef.current;
      const staffId = staffColumn(info.resourceId, shown);
      const row = stateRef.current.rows.find((r) => r.uid === uid);
      const blocked = staffId && blockedStaffFor(row, svcs || [], [staffId]);
      if (blocked) {
        const t = blockedDropToast(
          blocked.serviceName,
          staffDisplayName(shown, staffId),
        );
        notifyError(t.note, { title: t.title });
        return;
      }
      run({
        type: "tempDrop",
        uid,
        time,
        day: date,
        staffId,
        services: svcs,
      });
    },
    [run],
  );

  // ---- Derived -----------------------------------------------------------
  // The drawer render condition, exactly. A resize to mobile hides it (the
  // mobile sheet renders the same draft). Walk-ins never open a session:
  // they go through the waitlist.
  const panelOpen = state.open && !isMobile;
  // The mobile new-appointment sheet renders the same draft on phones.
  const sheetOpen = state.open && isMobile;
  // The draft's day (the viewed day only as the value before any open).
  const day = state.day || viewedDay;
  const { rows } = state;
  // Temp events render only while the draft's day is on screen.
  const draftInView = panelOpen && isDayInView(day, viewType, selectedDate);

  const fallbackResourceId = filteredEmployees?.[0]?.id || "";
  // Reschedule edits the appointment in place: its own events are hidden
  // while the drawer is open and the temp events carry the client's name.
  const hiddenTransactionId =
    panelOpen && state.mode === "edit"
      ? state.sourceTxn?.transaction.id || null
      : null;
  const sourceClient = state.mode === "edit" ? state.sourceTxn?.client : null;
  const sourceFirstName = sourceClient?.first_name ?? "";
  const sourceLastName = sourceClient?.last_name ?? "";
  const tempEvents = useMemo(
    () =>
      draftInView
        ? buildTempEvents({
            rows,
            day,
            activeUid: state.activeUid,
            clientUuid: state.clientUuid,
            services: salonServices || [],
            fallbackResourceId,
            client: sourceFirstName
              ? { firstName: sourceFirstName, lastName: sourceLastName }
              : undefined,
          })
        : [],
    [
      draftInView,
      rows,
      day,
      state.activeUid,
      state.clientUuid,
      salonServices,
      fallbackResourceId,
      sourceFirstName,
      sourceLastName,
    ],
  );

  // The time the Day view should keep on-screen: the active row's start, else
  // the first scheduled row's.
  const activeRow =
    rows.find((r) => r.uid === state.activeUid && r.time) ||
    rows.find((r) => r.salonServiceId && r.time) ||
    null;
  const scrollTimeOfDay = draftInView && activeRow ? activeRow.time : null;

  // Day-view columns blocked for the active service (hatched + "Doesn't
  // offer X"; a click / drop there is refused above).
  const activeServiceId =
    rows.find((r) => r.uid === state.activeUid)?.salonServiceId ?? "";
  const shownStaffKey = (filteredEmployees || []).map((e) => e.id).join(",");
  const blockedStaff = useMemo(
    () =>
      panelOpen
        ? blockedStaffFor(
            { salonServiceId: activeServiceId } as ServiceRow,
            salonServices || [],
            shownStaffKey ? shownStaffKey.split(",") : [],
          )
        : null,
    [panelOpen, activeServiceId, salonServices, shownStaffKey],
  );

  const focusMode =
    panelOpen && (state.mode === "edit" || state.mode === "rebook");

  // Reschedule / Rebook are a focused full-screen surface: publish to the
  // app-level SchedulingFocus context (hides the nav bar). External sync only.
  useEffect(() => {
    setSchedulingFocus(focusMode);
    return () => setSchedulingFocus(false);
  }, [focusMode, setSchedulingFocus]);

  // Escape interrupts the draft (the Drawer's own closeOnEscape is off). Not
  // while the discard prompt is up, so its Escape ("Keep editing") wins.
  const { discardPromptOpen } = state;
  useEffect(() => {
    if (!panelOpen || discardPromptOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        requestClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [panelOpen, discardPromptOpen, requestClose]);

  return {
    panelOpen,
    sheetOpen,
    focusMode,
    mode: state.mode,
    sourceTxn: state.sourceTxn,
    day,
    rows,
    clientUuid: state.clientUuid,
    activeUid: state.activeUid,
    discardPromptOpen,
    tempEvents,
    hiddenTransactionId,
    scrollTimeOfDay,
    blockedStaff,
    // Entry points
    startNewAppointment,
    startBlankAppointment,
    startFromSlot,
    startReschedule,
    startRebook,
    // Exit
    requestClose,
    requestCloseThen,
    close,
    dismissDiscard,
    discard,
    handleCreated,
    handleEditSuccess,
    // Form bindings
    handleRowsChange,
    handleClientChange,
    handleActiveChange,
    // Calendar routing while open
    handleCalendarSelectEvent,
    handleCalendarSelectSlot,
    handleCalendarDrop,
  };
}

export type BookingSession = ReturnType<typeof useBookingSession>;
